// Hook to check user onboarding status
// Determines if a user needs to complete onboarding flow

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useUser } from '../contextapi/UserContext';
import { supabase } from '../utils/supabaseClient';
import { queryKeys } from '../utils/queryKeys';

export interface OnboardingStatus {
  isOnboarded: boolean;
  isLoading: boolean;
  error: string | null;
  needsOnboarding: boolean;
  hasWorkspace: boolean;
  currentStep?: number;
}

type OnboardingData = {
  isOnboarded: boolean;
  hasWorkspace: boolean;
  currentStep: number;
};

const fetchOnboardingStatus = async (
  userId: string
): Promise<OnboardingData> => {
  const { data: profileData, error: profileError } = await supabase.rpc(
    'get_user_profile',
    { user_id: userId }
  );

  if (profileError) {
    if (
      profileError.code === 'PGRST116' ||
      profileError.code === 'PGRST301' ||
      profileError.message?.includes('403')
    ) {
      return { isOnboarded: false, hasWorkspace: false, currentStep: 1 };
    }
    throw profileError;
  }

  if (!profileData || profileData.length === 0) {
    return { isOnboarded: false, hasWorkspace: false, currentStep: 1 };
  }

  const profile = profileData[0];
  const isOnboarded = profile.is_onboarded || false;
  const hasWorkspace = !!profile.tenant_id;
  const onboarding = profile.onboarding || {};
  const currentStep = onboarding.step || (isOnboarded ? 3 : 1);

  return { isOnboarded, hasWorkspace, currentStep };
};

export const useOnboardingStatus = (): OnboardingStatus & {
  refresh: () => void;
} => {
  const { user } = useUser();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.user.onboarding(user?.id ?? ''),
    queryFn: () => fetchOnboardingStatus(user!.id),
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });

  const refresh = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.user.onboarding(user?.id ?? ''),
    });
  };

  return {
    isOnboarded: data?.isOnboarded ?? false,
    isLoading,
    error: error instanceof Error ? error.message : null,
    needsOnboarding: !(data?.isOnboarded ?? false),
    hasWorkspace: data?.hasWorkspace ?? false,
    currentStep: data?.currentStep,
    refresh,
  };
};
